import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setTelegramApp, setTelegramUser, setTelegramTheme, setTelegramReady } from '../redux/slices/telegramSlice';
import { safeOpenTelegramLink } from '../utils/telegramWait';

export const useTelegramApp = () => {
    const dispatch = useDispatch();
    const [isReady, setIsReady] = useState(false);
    const [isTelegramApp, setIsTelegramApp] = useState(false);
    const [telegramUser, setTelegramUserState] = useState(null);
    const [telegramTheme, setTelegramThemeState] = useState('light');

    // Функция для обновления игровых данных
    const refreshGameData = useCallback(() => {
        // Вызываем событие для обновления данных
        window.dispatchEvent(new CustomEvent('telegramAppActivated'));
        console.log('🔄 Telegram WebApp активирован - обновляем игровые данные');
    }, []);

    // Инициализация Telegram WebApp
    const initializeTelegramApp = useCallback(async () => {
        console.log('🔌 useTelegramApp: Хук вызывается...');
        
        // Проверяем, есть ли Telegram WebApp
        if (window.Telegram?.WebApp) {
            console.log('📱 Telegram WebApp обнаружен');
            setIsTelegramApp(true);
            dispatch(setTelegramApp({ isTelegramApp: true, telegramUser: null, isReady: false, telegramTheme: 'light' }));
            
            const webApp = window.Telegram.WebApp;
            
            // Инициализируем WebApp
            webApp.ready();
            console.log('✅ Telegram WebApp готов');
            
            // Получаем данные пользователя
            if (webApp.initDataUnsafe?.user) {
                const user = webApp.initDataUnsafe.user;
                console.log('👤 Пользователь Telegram:', user);
                
                setTelegramUserState(user);
                dispatch(setTelegramUser(user));
                
                // Устанавливаем тему
                const theme = webApp.colorScheme || 'light';
                setTelegramThemeState(theme);
                dispatch(setTelegramTheme(theme));
                
                console.log('🎨 Тема Telegram:', theme);
            } else {
                console.log('⚠️ Данные пользователя Telegram не найдены');
            }
            
            // Устанавливаем готовность
            setIsReady(true);
            dispatch(setTelegramReady(true));
            
            // ✅ Слушаем активацию приложения
            webApp.onEvent('appActivated', () => {
                console.log('🔄 Telegram WebApp активирован');
                refreshGameData();
            });
            
            // ✅ Слушаем возвращение в приложение
            webApp.onEvent('appReturned', () => {
                console.log('🔄 Telegram WebApp возвращен в фокус');
                refreshGameData();
            });
            
            // Слушаем изменения темы
            webApp.onEvent('themeChanged', () => {
                const newTheme = webApp.colorScheme || 'light';
                console.log('🎨 Тема Telegram изменилась:', newTheme);
                setTelegramThemeState(newTheme);
                dispatch(setTelegramTheme(newTheme));
            });
            
            // Слушаем изменения viewport
            webApp.onEvent('viewportChanged', () => {
                console.log('📱 Viewport Telegram изменился');
            });
            
            // Слушаем изменения mainButton
            webApp.onEvent('mainButtonClicked', () => {
                console.log('🔘 Main button Telegram нажат');
            });
            
            // Слушаем изменения backButton
            webApp.onEvent('backButtonClicked', () => {
                console.log('⬅️ Back button Telegram нажат');
            });
            
            // Слушаем изменения settingsButton
            webApp.onEvent('settingsButtonClicked', () => {
                console.log('⚙️ Settings button Telegram нажат');
            });
            
            // Слушаем изменения invoiceClosed
            webApp.onEvent('invoiceClosed', (status) => {
                console.log('💰 Invoice Telegram закрыт:', status);
            });
            
            // Слушаем изменения popupClosed
            webApp.onEvent('popupClosed', (buttonId) => {
                console.log('🚪 Popup Telegram закрыт:', buttonId);
            });
            
            // Слушаем изменения qrTextReceived
            webApp.onEvent('qrTextReceived', (data) => {
                console.log('📱 QR код Telegram получен:', data);
            });
            
            // Слушаем изменения clipboardTextReceived
            webApp.onEvent('clipboardTextReceived', (data) => {
                console.log('📋 Текст из буфера обмена Telegram получен:', data);
            });
            
            // Слушаем изменения writeAccessRequested
            webApp.onEvent('writeAccessRequested', (data) => {
                console.log('✍️ Запрос на запись в буфер обмена Telegram:', data);
            });
            
            // Слушаем изменения contactRequested
            webApp.onEvent('contactRequested', (data) => {
                console.log('👥 Запрос контакта Telegram:', data);
            });
            
            // Слушаем изменения customEvent
            webApp.onEvent('customEvent', (data) => {
                console.log('🎯 Кастомное событие Telegram:', data);
            });
            
        } else {
            console.log('🌐 Telegram WebApp не обнаружен, используем обычный браузер');
            setIsTelegramApp(false);
            dispatch(setTelegramApp({ isTelegramApp: false, telegramUser: null, isReady: true, telegramTheme: 'light' }));
            setIsReady(true);
        }
    }, [dispatch, refreshGameData]);

    // Инициализация при монтировании
    useEffect(() => {
        initializeTelegramApp();
    }, [initializeTelegramApp]);

    // Функция для получения initData
    const getInitData = useCallback(() => {
        if (window.Telegram?.WebApp?.initData) {
            return window.Telegram.WebApp.initData;
        }
        return null;
    }, []);

    // Функция для получения данных пользователя
    const getUser = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
            return window.Telegram.WebApp.initDataUnsafe.user;
        }
        return null;
    }, []);

    // Функция для получения темы
    const getTheme = useCallback(() => {
        if (window.Telegram?.WebApp?.colorScheme) {
            return window.Telegram.WebApp.colorScheme;
        }
        return 'light';
    }, []);

    // Функция для получения startParam
    const getStartParam = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.start_param) {
            return window.Telegram.WebApp.initDataUnsafe.start_param;
        }
        return null;
    }, []);

    // Функция для получения chatType
    const getChatType = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat_type) {
            return window.Telegram.WebApp.initDataUnsafe.chat_type;
        }
        return null;
    }, []);

    // Функция для получения chatInstance
    const getChatInstance = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat_instance) {
            return window.Telegram.WebApp.initDataUnsafe.chat_instance;
        }
        return null;
    }, []);

    // Функция для получения chatId
    const getChatId = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.id) {
            return window.Telegram.WebApp.initDataUnsafe.chat.id;
        }
        return null;
    }, []);

    // Функция для получения chatTitle
    const getChatTitle = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.title) {
            return window.Telegram.WebApp.initDataUnsafe.chat.title;
        }
        return null;
    }, []);

    // Функция для получения chatUsername
    const getChatUsername = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.username) {
            return window.Telegram.WebApp.initDataUnsafe.chat.username;
        }
        return null;
    }, []);

    // Функция для получения chatType
    const getChatTypeFromChat = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.type) {
            return window.Telegram.WebApp.initDataUnsafe.chat.type;
        }
        return null;
    }, []);

    // Функция для получения chatPhoto
    const getChatPhoto = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.photo) {
            return window.Telegram.WebApp.initDataUnsafe.chat.photo;
        }
        return null;
    }, []);

    // Функция для получения chatPermissions
    const getChatPermissions = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.permissions) {
            return window.Telegram.WebApp.initDataUnsafe.chat.permissions;
        }
        return null;
    }, []);

    // Функция для получения chatSlowModeDelay
    const getChatSlowModeDelay = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.slow_mode_delay) {
            return window.Telegram.WebApp.initDataUnsafe.chat.slow_mode_delay;
        }
        return null;
    }, []);

    // Функция для получения chatUnrestrictBoostCount
    const getChatUnrestrictBoostCount = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.unrestrict_boost_count) {
            return window.Telegram.WebApp.initDataUnsafe.chat.unrestrict_boost_count;
        }
        return null;
    }, []);

    // Функция для получения chatIsForum
    const getChatIsForum = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.is_forum) {
            return window.Telegram.WebApp.initDataUnsafe.chat.is_forum;
        }
        return null;
    }, []);

    // Функция для получения chatActiveUsernames
    const getChatActiveUsernames = useCallback(() => {
        if (window.Telegram.WebApp?.initDataUnsafe?.chat?.active_usernames) {
            return window.Telegram.WebApp.initDataUnsafe.chat.active_usernames;
        }
        return null;
    }, []);

    // Функция для получения chatEmojiStatus
    const getChatEmojiStatus = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.emoji_status) {
            return window.Telegram.WebApp.initDataUnsafe.chat.emoji_status;
        }
        return null;
    }, []);

    // Функция для получения chatBio
    const getChatBio = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.bio) {
            return window.Telegram.WebApp.initDataUnsafe.chat.bio;
        }
        return null;
    }, []);

    // Функция для получения chatHasPrivateForwards
    const getChatHasPrivateForwards = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.has_private_forwards) {
            return window.Telegram.WebApp.initDataUnsafe.chat.has_private_forwards;
        }
        return null;
    }, []);

    // Функция для получения chatHasRestrictedVoiceAndVideoMessages
    const getChatHasRestrictedVoiceAndVideoMessages = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.has_restricted_voice_and_video_messages) {
            return window.Telegram.WebApp.initDataUnsafe.chat.has_restricted_voice_and_video_messages;
        }
        return null;
    }, []);

    // Функция для получения chatJoinToSendMessages
    const getChatJoinToSendMessages = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.join_to_send_messages) {
            return window.Telegram.WebApp.initDataUnsafe.chat.join_to_send_messages;
        }
        return null;
    }, []);

    // Функция для получения chatJoinByRequest
    const getChatJoinByRequest = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.join_by_request) {
            return window.Telegram.WebApp.initDataUnsafe.chat.join_by_request;
        }
        return null;
    }, []);

    // Функция для получения chatHasHiddenMembers
    const getChatHasHiddenMembers = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.has_hidden_members) {
            return window.Telegram.WebApp.initDataUnsafe.chat.has_hidden_members;
        }
        return null;
    }, []);

    // Функция для получения chatHasAggressiveAntiSpamEnabled
    const getChatHasAggressiveAntiSpamEnabled = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.has_aggressive_anti_spam_enabled) {
            return window.Telegram.WebApp.initDataUnsafe.chat.has_aggressive_anti_spam_enabled;
        }
        return null;
    }, []);

    // Функция для получения chatHasProtectedContent
    const getChatHasProtectedContent = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.has_protected_content) {
            return window.Telegram.WebApp.initDataUnsafe.chat.has_protected_content;
        }
        return null;
    }, []);

    // Функция для получения chatInviteLink
    const getChatInviteLink = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.invite_link) {
            return window.Telegram.WebApp.initDataUnsafe.chat.invite_link;
        }
        return null;
    }, []);

    // Функция для получения chatPinnedMessage
    const getChatPinnedMessage = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.pinned_message) {
            return window.Telegram.WebApp.initDataUnsafe.chat.pinned_message;
        }
        return null;
    }, []);

    // Функция для получения chatStickerSetName
    const getChatStickerSetName = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.sticker_set_name) {
            return window.Telegram.WebApp.initDataUnsafe.chat.sticker_set_name;
        }
        return null;
    }, []);

    // Функция для получения chatCanSetStickerSet
    const getChatCanSetStickerSet = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.can_set_sticker_set) {
            return window.Telegram.WebApp.initDataUnsafe.chat.can_set_sticker_set;
        }
        return null;
    }, []);

    // Функция для получения chatLinkedChatId
    const getChatLinkedChatId = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.linked_chat_id) {
            return window.Telegram.WebApp.initDataUnsafe.chat.linked_chat_id;
        }
        return null;
    }, []);

    // Функция для получения chatLocation
    const getChatLocation = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.location) {
            return window.Telegram.WebApp.initDataUnsafe.chat.location;
        }
        return null;
    }, []);

    // Функция для получения chatMessageAutoDeleteTime
    const getChatMessageAutoDeleteTime = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.message_auto_delete_time) {
            return window.Telegram.WebApp.initDataUnsafe.chat.message_auto_delete_time;
        }
        return null;
    }, []);

    // Функция для получения chatHasMediaSpoilers
    const getChatHasMediaSpoilers = useCallback(() => {
        if (window.Telegram?.WebApp?.initDataUnsafe?.chat?.has_media_spoilers) {
            return window.Telegram.WebApp.initDataUnsafe.chat.has_media_spoilers;
        }
        return null;
    }, []);

    // Функция для отправки сообщений в Telegram
    const share = useCallback(async (text, url) => {
        if (window.Telegram?.WebApp) {
            // Используем безопасную функцию с ожиданием загрузки API
            const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
            const success = await safeOpenTelegramLink(shareUrl);
            
            if (!success) {
                console.warn('⚠️ Не удалось открыть ссылку через Telegram API, используем fallback');
                window.open(shareUrl, '_blank');
            }
        } else {
            // Если не в Telegram WebApp, открываем в новой вкладке
            const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
            window.open(shareUrl, '_blank');
        }
    }, []);

    return {
        isTelegramApp,
        telegramUser,
        isReady,
        telegramTheme,
        getInitData,
        getUser,
        getTheme,
        getStartParam,
        getChatType,
        getChatInstance,
        getChatId,
        getChatTitle,
        getChatUsername,
        getChatTypeFromChat,
        getChatPhoto,
        getChatPermissions,
        getChatSlowModeDelay,
        getChatUnrestrictBoostCount,
        getChatIsForum,
        getChatActiveUsernames,
        getChatEmojiStatus,
        getChatBio,
        getChatHasPrivateForwards,
        getChatHasRestrictedVoiceAndVideoMessages,
        getChatJoinToSendMessages,
        getChatJoinByRequest,
        getChatHasHiddenMembers,
        getChatHasAggressiveAntiSpamEnabled,
        getChatHasProtectedContent,
        getChatInviteLink,
        getChatPinnedMessage,
        getChatStickerSetName,
        getChatCanSetStickerSet,
        getChatLinkedChatId,
        getChatLocation,
        getChatMessageAutoDeleteTime,
        getChatHasMediaSpoilers,
        share,
    };
}; 